import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import {
  Outlet,
  useLocation,
  useMatches,
  useNavigate,
  useNavigationType,
  useOutlet,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { isNative } from "../../native";
import OfflineNotice from "../feedback/OfflineNotice";
import BottomNavBar from "./BottomNavBar";
import Icon, { type IconName } from "../primitives/Icon";
import AppBar, { type AppBarVariant } from "./AppBar";
import { useAppBarSlotValue } from "./AppBarSlotContext";
import { AppShell } from "@astryxdesign/core/AppShell";
import { DesktopSideNav, DesktopTopNav } from "./DesktopChrome";
import { useIsDesktop } from "../../hooks/useMediaQuery";
import { useTranslation } from "react-i18next";
import Sheet from "../feedback/Sheet";
import styles from "./Layout.module.css";
import type { OverlayNavigationState } from "../../hooks/useOverlayNavigate";
import { requestBottomNavReload } from "../../hooks/useBottomNavReload";
import { useBrowseResumeRefresh } from "../../hooks/useBrowseResumeRefresh";
import { useActivityStore } from "../../store/activityStore";
import { useAuthStore } from "../../store/authStore";
import { useBackDispatcher } from "../../navigation/dispatchBack";
import { useRequireAuth } from "../../navigation/useRequireAuth";
import { useActivityNavigator } from "../../navigation/useActivityNavigator";
import { getActivityRouteRule } from "../../navigation/activityRoutes";
import { renderMainTabByKind, inferMainTabKindFromPath, type MainTabKind } from "./mainTabRegistry";
import { MainTabActiveContext } from "./mainTabActiveContext";
// ActivityHost 가 흡수한 sub-activity kind 집합 — single source of truth.
// 새 sub-activity 흡수 시 ActivityHost 의 SUB_ACTIVITY_RESOLVERS 에만 등록하면
// 이 set 도 자동 갱신.
import { ACTIVITY_HOST_HANDLED_KINDS } from "./ActivityHost";

interface BottomNavTab {
  path: string;
  /** i18n 키 (ns=card). 렌더 시 t() 로 푼다 */
  labelKey: string;
  /** 디자인 시스템 아이콘 (bottomNav 메뉴, 활성). 미지정 시 icon 텍스트 폴백. */
  iconName?: IconName;
  /** 비활성 상태 아이콘. 미지정 시 iconName 을 그대로 사용 (opacity 로만 구분). */
  iconNameInactive?: IconName;
  icon: string;
  /** 아이콘 좌우 반전 (쿠지 티켓 — Figma 정본 scaleX(-1)). */
  flipX?: boolean;
  /** 중앙 강조 탭 (스캔) — 기획확정안 §7 */
  emphasized?: boolean;
  /** true 면 비로그인 상태에서 탭하면 navigate 대신 auth activity 부터 띄움. */
  requiresAuth?: boolean;
}

/**
 * 메인 탭 — 기획확정안 §7: 홈·검색·스캔·컬렉션·내 정보, **스캔을 중앙에 가장 눈에 띄게**.
 * 라벨은 i18n 키(card.nav.*)로 두고 렌더 시점에 t() 로 푼다.
 */
const TABS: BottomNavTab[] = [
  { path: "/", labelKey: "nav.home", iconName: "Home", icon: "⌂" },
  { path: "/search", labelKey: "nav.search", iconName: "Search", icon: "🔍" },
  // 중앙 = 스캔 (앱의 핵심 진입점 §2). 카메라 엔진이 없는 웹에서는 노출하지 않는다.
  { path: "/scan", labelKey: "nav.scan", iconName: "Camera", icon: "📷", emphasized: true },
  // 컬렉션 = 보관함(Storage). 하트(Wishlist)는 "찜"을 뜻해 컬렉션과 의미가 어긋난다.
  { path: "/collections", labelKey: "nav.collection", iconName: "Storage", icon: "🗂" },
  { path: "/me", labelKey: "nav.me", iconName: "My", icon: "●", requiresAuth: true },
];

/** 스캔 탭은 네이티브 앱 전용 — 웹(모바일·데스크탑)에서는 목록에서 제외. 테스트용 export. */
export function computeVisibleTabs(native: boolean): BottomNavTab[] {
  return native ? TABS : TABS.filter((tab) => tab.path !== "/scan");
}

// isNative() 는 런타임 내내 정적이므로 모듈 레벨에서 1회 계산.
const VISIBLE_TABS = computeVisibleTabs(isNative());

/** 경로 → VISIBLE_TABS 인덱스 (없으면 -1 — 어떤 탭도 활성 표시되지 않음). */
function tabIndexOf(path: string): number {
  return VISIBLE_TABS.findIndex((tab) => tab.path === path);
}

const OVERLAY_EXIT_MS = 280;

export interface RouteHandle {
  appBar?: boolean;
  appBarVariant?: AppBarVariant;
  appBarTitle?: string;
  bottomNav?: boolean;
  /**
   * 라우트 표현 모드:
   *  - "page" (default): 일반 페이지 (탭/AppBar 안에서 슬라이드 전환)
   *  - "overlay": 오른쪽에서 슬라이드인하는 오버레이 (상세 페이지)
   *  - "sheet": 하단에서 올라오는 bottom sheet (라우트 진입 페이지가 unmount 됨에 주의 —
   *    하위 페이지 상태 보존이 필요한 팝업은 store-based 패턴 사용)
   *
   * 풀스크린 팝업은 store-based (`<AddressFormPopup />`) 또는 SubActivity (ActivityHost)
   * 로 처리.
   */
  presentation?: "page" | "overlay" | "sheet";
  overlayFallbackPath?: string;
  /**
   * AppBar 뒤로가기 / overlay close 시 항상 이 경로로 replace navigate.
   * history.back 보다 우선. 결제/배송 신청 후 status 페이지처럼 흐름의 종착점이라
   * 이전 단계로 돌아가는 게 부적절한 경우에 사용.
   */
  closeFallbackPath?: string;
}

interface ChromeState {
  appBar: boolean;
  appBarVariant: AppBarVariant;
  appBarTitle?: string;
  bottomNav: boolean;
  activeTab: number;
}

interface ExitingOverlayState {
  key: string;
  outlet: ReactNode;
  appBar: boolean;
  appBarVariant: AppBarVariant;
  appBarTitle?: string;
}

export default function Layout() {
  const navigate = useNavigate();
  const dispatchBack = useBackDispatcher();
  const { requireAuth } = useRequireAuth();
  const activityNav = useActivityNavigator();
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);
  const location = useLocation();
  const matches = useMatches();
  const navigationType = useNavigationType();
  const outlet = useOutlet();
  // 앱 백그라운드(≥120s) → 포그라운드 복귀 시 browse 탭(홈/쿠지/가챠) 데이터 최신화.
  useBrowseResumeRefresh();
  const backgroundOutletRef = useRef<ReactNode | null>(null);
  const backgroundChromeRef = useRef<ChromeState | null>(null);
  const backgroundAppBarRef = useRef<ReactNode | null>(null);
  const overlayShellKeyRef = useRef<string | null>(null);
  const overlayCloseTimerRef = useRef<number | null>(null);
  const overlayExitTimerRef = useRef<number | null>(null);
  const previousPathRef = useRef(location.pathname);
  const previousHadOverlayBackgroundRef = useRef(false);
  const lastOverlayRef = useRef<ExitingOverlayState | null>(null);
  const skipNextPopExitRef = useRef(false);
  const [isOverlayClosing, setIsOverlayClosing] = useState(false);
  const [exitingOverlay, setExitingOverlay] = useState<ExitingOverlayState | null>(null);
  const routeState = location.state as OverlayNavigationState | null;

  const handle = (matches[matches.length - 1]?.handle ?? {}) as RouteHandle;
  const appBar = handle.appBar ?? true;
  const appBarVariant: AppBarVariant = handle.appBarVariant ?? "home";
  // 페이지가 올려보낸 제목·액션이 있으면 그쪽이 이긴다 (예: 카드 상세 = 카드명 + 공유).
  // activity 소유 슬롯은 ActivityHost 앱바 몫이므로 여기선 잡히지 않는다 (owner 생략).
  const appBarSlot = useAppBarSlotValue();

  const bottomNav = handle.bottomNav ?? true;

  // ActivityHost 가 흡수한 sub-activity URL 인지 판단.
  // 흡수된 경우 Layout 은 Outlet swap 을 하지 않고 부모 main tab 을 직접 렌더한다.
  // → KujiListPage 같은 부모가 mount 상태 유지 (스크롤/필터/입력 보존).
  const activityRule = getActivityRouteRule(location.pathname);
  const isHostedByActivityHost = !!activityRule && ACTIVITY_HOST_HANDLED_KINDS.has(activityRule.kind);
  // 메인 탭 path 와 흡수된 sub-activity URL 양쪽 모두 같은 React 위치 (mainTabRegistry)
  // 에 렌더 → 메인 탭이 sub-activity 진입/종료에서 unmount 되지 않는다.
  // /scan 은 keep-alive 탭 슬롯이 아니라 activity 로 뜨므로 부모 경로 목록에서 제외
  const HOSTED_PARENT_PATHS = new Set([
    ...VISIBLE_TABS.map((tab) => tab.path).filter((p) => p !== "/scan"),
    "/notifications",
  ]);
  const isHostedParentPath = HOSTED_PARENT_PATHS.has(location.pathname);
  // sub-activity 가 열려있으면 그 활동의 fallbackPath 로 부모 main tab 추론.
  // (예: /prize/X 는 URL 만 보면 'kuji' 로 매핑되지만, 픽쿠함에서 진입했다면
  //  fallbackPath='/pickubox' 라서 'pickubox' 가 맞다.)
  //
  // ⚠ top 하나만 보면 안 된다 — activity 가 겹쳐 뜨면(카드 상세 → 공유) top 의
  // fallbackPath 가 '/cards/123' 처럼 탭이 아닌 경로여서 추론이 null 로 빠지고, 그러면
  // Layout 이 keep-alive 슬롯을 버리고 Outlet 을 다시 그려 **부모 탭이 remount** 된다
  // (스크롤·필터 유실). 위에서 아래로 내려가며 메인 탭으로 해석되는 첫 fallbackPath 를
  // 베이스로 삼는다.
  const topHostedActivityFallback = useActivityStore((s) => {
    let lowest: string | null = null;
    for (let i = s.activities.length - 1; i >= 0; i--) {
      const a = s.activities[i];
      if (a.kind === "auth" || !ACTIVITY_HOST_HANDLED_KINDS.has(a.kind)) continue;
      // fallbackPath 에는 쿼리가 붙어 있을 수 있다 ("/support?tab=inquiry") — 추론은 path 만.
      const base = a.fallbackPath.split("?")[0];
      if (inferMainTabKindFromPath(base)) return base;
      lowest = base;
    }
    return lowest;
  });
  // 닫힘 transition 동안 활동은 이미 사라졌지만 URL 이 아직 sub-activity URL 일 수
  // 있다 (Zustand sync 가 RR 의 location 업데이트보다 먼저 commit 되는 케이스).
  // 그 한 프레임 동안 location.pathname='/prize/X' 만 보고 'kuji' 로 추론하면 잘못된
  // 메인 탭이 깜빡임 → 마지막 hosted fallback 을 ref 로 보관해서 사용.
  const lastHostedFallbackRef = useRef<string | null>(null);
  if (topHostedActivityFallback !== null) {
    lastHostedFallbackRef.current = topHostedActivityFallback;
  }
  // URL 자체가 sub-activity 패턴인데 활동 stack 이 비어있으면 transition-out 중.
  const activityRuleAtUrl = getActivityRouteRule(location.pathname);
  const isSubActivityUrl = !!activityRuleAtUrl && ACTIVITY_HOST_HANDLED_KINDS.has(activityRuleAtUrl.kind);
  const isTransitioningOut = isSubActivityUrl && topHostedActivityFallback === null;
  const pathForInference = topHostedActivityFallback
    ?? (isTransitioningOut ? lastHostedFallbackRef.current : null)
    ?? location.pathname;
  const inferredKind = inferMainTabKindFromPath(pathForInference);
  const hostedParentKind = (isHostedByActivityHost || isHostedParentPath) ? inferredKind : null;
  const useMainTabSlot = hostedParentKind !== null;

  // Keep-alive: 한 번이라도 방문한 메인 탭은 unmount 시키지 않고 display:none 으로
  // 숨겨서 scroll/필터/입력 상태를 DOM 에 자연 보존. 첫 방문에서만 mount 됨.
  const [visitedKinds, setVisitedKinds] = useState<Set<MainTabKind>>(() =>
    hostedParentKind ? new Set<MainTabKind>([hostedParentKind]) : new Set<MainTabKind>(),
  );
  const slotRefs = useRef<Map<MainTabKind, HTMLDivElement>>(new Map());
  useLayoutEffect(() => {
    if (!hostedParentKind) return;
    setVisitedKinds((prev) => {
      if (prev.has(hostedParentKind)) return prev;
      const next = new Set(prev);
      next.add(hostedParentKind);
      return next;
    });
  }, [hostedParentKind]);

  // 템플릿에는 라우트 기반 overlay 상세가 없음 — 필요 시 여기에 패턴 추가.
  const isRouteOverlayDetail = false;
  // docs/12 D1 + D7/D8/D9: any non-auth SubActivity in the store is treated
  // as an overlay so the underlying main page (e.g. /me/box) stays mounted
  // via backgroundOutletRef. AuthActivity has its own fullscreen host
  // (RootLayout → ActivityHost), so it is excluded here.
  const hasStoreSubActivity = useActivityStore((s) =>
    s.activities.some((a) => a.kind !== "auth"),
  );
  const topStoreActivityAction = useActivityStore((s) => {
    const activity = [...s.activities].reverse().find((a) => a.kind !== "auth");
    return activity?.appBarAction ?? null;
  });
  const isSheet = handle.presentation === "sheet";
  const isOverlay =
    isSheet ||
    handle.presentation === "overlay" ||
    routeState?.presentation === "overlay" ||
    isRouteOverlayDetail ||
    hasStoreSubActivity;
  const hasOverlayBackground = isOverlay && backgroundOutletRef.current !== null;
  const isBack = navigationType === "POP";
  const isMainMenuSwitch =
    isMainTabPath(previousPathRef.current) &&
    isMainTabPath(location.pathname) &&
    previousPathRef.current !== location.pathname;
  const overlayShellKey = getOverlayShellKey(location.pathname, routeState, overlayShellKeyRef.current);

  const { t } = useTranslation("card");

  /**
   * 라우트 제목은 App.tsx 에 **i18n 키**로 적혀 있다 (`nav.route.*`) — 표시 언어를
   * 바꿨는데 앱바만 한국어로 남는 걸 막으려면 라우트 테이블에 문자열을 박으면 안 된다.
   * 슬롯 제목(카드명 등)은 이미 번역된 값이라 그대로 쓴다.
   */
  const routeTitle = handle.appBarTitle
    ? (handle.appBarTitle.startsWith("nav.route.") ? t(handle.appBarTitle) : handle.appBarTitle)
    : undefined;
  const appBarTitle = appBarSlot.title ?? routeTitle;
  const isDesktop = useIsDesktop();
  const activeTab = VISIBLE_TABS.findIndex((t) => {
    if (t.path === "/") return location.pathname === "/";
    return location.pathname.startsWith(t.path);
  });

  // 페이지 자체가 지정한 뒤로가기 종착점 — closeFallbackPath 가 있으면 history.back 대신
  // 항상 그 경로로 navigate.
  //
  // overlayDepth>0 일 때 단순 replace 만 하면 overlay 진입 단계의 history entry 들이
  // 그대로 남아있어, fallback 페이지에서 다시 back 시 shipping/request 같은 중간 페이지로
  // 빠져나가는 history 꼬임이 생긴다. 따라서:
  //   1) navigate(-depth) 로 overlay 진입 entry 들을 일괄 pop
  //   2) microtask 후 fallback 으로 replace — pop 후 진입한 entry(=overlay 진입 직전 페이지)
  //      를 fallback 으로 덮어써 깔끔한 single-entry 로 정리
  const closeWithFallback = (path: string) => {
    const depth = routeState?.overlayDepth ?? 0;
    if (depth > 0) {
      navigate(-depth);
      setTimeout(() => navigate(path, { replace: true }), 0);
    } else {
      navigate(path, { replace: true });
    }
  };

  const handleBackPressed = () => {
    if (handle.closeFallbackPath) {
      closeWithFallback(handle.closeFallbackPath);
      return;
    }
    navigate(-1);
  };

  if (!isOverlay) {
    backgroundOutletRef.current = outlet;
    backgroundChromeRef.current = {
      appBar,
      appBarVariant,
      appBarTitle,
      bottomNav,
      activeTab: activeTab >= 0 ? activeTab : 0,
    };
    backgroundAppBarRef.current = appBar ? (
      <AppBar
        variant={appBarVariant}
        title={appBarTitle}
        rightAction={appBarSlot.action}
        onBackPressed={handleBackPressed}
        chromeAction={topStoreActivityAction === 'close' ? 'close' : 'back'}
      />
    ) : null;
    overlayShellKeyRef.current = null;
  } else {
    overlayShellKeyRef.current = overlayShellKey;
  }

  if (hasOverlayBackground) {
    lastOverlayRef.current = {
      key: overlayShellKey,
      outlet,
      appBar,
      appBarVariant,
      appBarTitle,
    };
  }

  const baseChrome = isOverlay
    ? backgroundChromeRef.current ?? getFallbackChrome(routeState, location.pathname)
    : null;
  const shellAppBar = baseChrome?.appBar ?? appBar;
  const shellAppBarVariant = baseChrome?.appBarVariant ?? appBarVariant;
  const shellAppBarTitle = baseChrome?.appBarTitle ?? appBarTitle;
  const shellBottomNav = baseChrome?.bottomNav ?? bottomNav;
  const shellActiveTab = baseChrome?.activeTab ?? (activeTab >= 0 ? activeTab : 0);
  const fallbackShellAppBar = shellAppBar ? (
    <AppBar
      variant={shellAppBarVariant}
      title={shellAppBarTitle}
      // 페이지가 올려보낸 액션은 오버레이가 아닌 일반 페이지에서도 떠야 한다 —
      // 이 셸 앱바가 실제로 그려지는 경로다 (카드 상세의 공유가 여기로 나온다).
      rightAction={appBarSlot.action}
      onBackPressed={handleBackPressed}
      chromeAction={topStoreActivityAction === 'close' ? 'close' : 'back'}
    />
  ) : null;
  const shellAppBarNode = isOverlay
    ? backgroundAppBarRef.current ?? fallbackShellAppBar
    : fallbackShellAppBar;

  useLayoutEffect(() => {
    if (
      previousHadOverlayBackgroundRef.current &&
      !hasOverlayBackground &&
      navigationType === "POP" &&
      lastOverlayRef.current !== null
    ) {
      if (skipNextPopExitRef.current) {
        skipNextPopExitRef.current = false;
      } else {
        setExitingOverlay(lastOverlayRef.current);
      }
    }

    previousHadOverlayBackgroundRef.current = hasOverlayBackground;
  }, [hasOverlayBackground, location.key, navigationType]);

  useEffect(() => {
    setIsOverlayClosing(false);
    previousPathRef.current = location.pathname;
  }, [location.key, location.pathname]);

  useEffect(() => {
    if (exitingOverlay === null) return;

    if (overlayExitTimerRef.current !== null) {
      window.clearTimeout(overlayExitTimerRef.current);
    }

    overlayExitTimerRef.current = window.setTimeout(() => {
      setExitingOverlay(null);
      lastOverlayRef.current = null;
      overlayExitTimerRef.current = null;
    }, OVERLAY_EXIT_MS);
  }, [exitingOverlay]);

  useEffect(() => {
    return () => {
      if (overlayCloseTimerRef.current !== null) {
        window.clearTimeout(overlayCloseTimerRef.current);
      }
      if (overlayExitTimerRef.current !== null) {
        window.clearTimeout(overlayExitTimerRef.current);
      }
    };
  }, []);

  const closeOverlay = () => {
    if (hasStoreSubActivity) {
      dispatchBack(topStoreActivityAction === "close" ? "close-button" : "in-app");
      return;
    }

    // 페이지가 지정한 종착점이 있으면 깊이/배경 무관하게 우선 적용 — overlay entry 들 청소 후 replace.
    if (handle.closeFallbackPath) {
      closeWithFallback(handle.closeFallbackPath);
      return;
    }
    // sheet 모드는 깊이와 무관하게 항상 exit 애니메이션 후 unmount.
    if (isSheet) {
      if (isOverlayClosing) return;
      setIsOverlayClosing(true);
      overlayCloseTimerRef.current = window.setTimeout(() => {
        skipNextPopExitRef.current = true;
        if ((routeState?.overlayDepth ?? 0) > 0) {
          navigate(-1);
        } else {
          navigate(
            routeState?.overlayFallbackPath ?? handle.overlayFallbackPath ?? "/",
            { replace: true },
          );
        }
      }, OVERLAY_EXIT_MS);
      return;
    }

    if ((routeState?.overlayDepth ?? 0) > 0) {
      navigate(-1);
      return;
    }

    if (hasOverlayBackground) {
      if (isOverlayClosing) return;
      setIsOverlayClosing(true);
      overlayCloseTimerRef.current = window.setTimeout(() => {
        skipNextPopExitRef.current = true;
        navigate(-1);
      }, OVERLAY_EXIT_MS);
      return;
    }

    navigate(routeState?.overlayFallbackPath ?? handle.overlayFallbackPath ?? "/", { replace: true });
  };

  // 모바일 하단 탭 + 데스크탑 SideNav 공용 핸들러.
  // 재탭=reload+scrollTop, requiresAuth 게이트, 홈 이탈만 push 나머지 replace (docs/12 D5/Main).
  const handleTabTap = (i: number) => {
    const tab = VISIBLE_TABS[i];
    if (!tab) return;
    // 스캔 탭 = 전체화면 activity (2026-07-31) — 탭 전환이 아니라 현재 화면 위에 뜨고,
    // X 로 닫으면 열었던 화면으로 복귀 (activityRoutes 의 scan rule 이 fallback 계산).
    if (tab.path === "/scan") {
      activityNav.open({ url: "/scan" });
      return;
    }
    const isAlreadyOnThisTab = location.pathname === tab.path;
    if (isAlreadyOnThisTab) {
      const kind = inferMainTabKindFromPath(tab.path);
      if (kind) {
        slotRefs.current.get(kind)?.scrollTo({ top: 0, behavior: "smooth" });
      }
      requestBottomNavReload(tab.path);
      return;
    }
    if (tab.requiresAuth && !isAuthenticated) {
      requireAuth({
        type: "navigate",
        to: tab.path,
        mode: "mainTab",
      });
      return;
    }
    const leavingHome = location.pathname === "/" && tab.path !== "/";
    navigate(tab.path, { replace: !leavingHome });
  };

  const shellInner = (
    <div className={styles.shell}>
      {/* 데스크탑에서는 메인 탭용 appBar(home/myPage)가 SideNav/TopNav 와 중복 → CSS 로 숨김 */}
      <div className={styles.appBarZone} data-appbar-variant={shellAppBarVariant ?? "home"}>
        {shellAppBarNode}
      </div>
      {/* FN-085 오프라인 배너 — 앱바 아래에 떠서 어느 화면에서든 같은 자리 */}
      <OfflineNotice />

      {/* 앱바·하단탭이 **떠 있으므로** 스크롤 영역이 그 높이만큼 위아래를 비운다.
          없는 화면에서는 그 여백을 되돌린다 (스캔처럼 화면을 꽉 쓰는 자리). */}
      <main
        className={[
          styles.main,
          shellBottomNav ? "" : styles.mainNoNav,
          shellAppBar ? "" : styles.mainNoAppBar,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {useMainTabSlot ? (
          // 메인 탭 4개 keep-alive. visitedKinds 에 들어간 슬롯만 mount, 비활성은
          // display:none 으로 숨김. 같은 탭 안에서 sub-activity 가 흡수돼도
          // (예: /shop ↔ /shop/:id) hostedParentKind 가 동일하므로 같은 슬롯이
          // active 유지. sub-activity 콘텐츠는 ActivityHost 가 별도 fixed overlay 로 렌더.
          <>
            {Array.from(visitedKinds).map((kind) => {
              const isActive = kind === hostedParentKind;
              return (
                <div
                  key={kind}
                  ref={(el) => {
                    if (el) slotRefs.current.set(kind, el);
                    else slotRefs.current.delete(kind);
                  }}
                  className={styles.routeMotion}
                  style={isActive ? undefined : { display: "none" }}
                  aria-hidden={!isActive}
                >
                  {/* 숨김 슬롯은 mount 는 유지되지만 "비활성" — 탭 페이지가 폴링 등을
                      게이트할 수 있도록 활성 여부를 컨텍스트로 주입. */}
                  <MainTabActiveContext.Provider value={isActive}>
                    {renderMainTabByKind(kind)}
                  </MainTabActiveContext.Provider>
                </div>
              );
            })}
          </>
        ) : hasOverlayBackground ? (
          <motion.div
            className={styles.routeMotion}
            variants={backgroundFadeVariants}
            initial="enter"
            animate="center"
            transition={overlayTransition}
          >
            {backgroundOutletRef.current}
          </motion.div>
        ) : (
          <AnimatePresence mode="popLayout" initial={false} custom={isBack}>
            <motion.div
              key={location.pathname}
              custom={isBack}
              variants={isMainMenuSwitch ? noMotionVariants : pageVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={isMainMenuSwitch ? noMotionTransition : pageTransition}
              className={styles.routeMotion}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        )}
      </main>

      {shellBottomNav && (
        <div className={styles.bottomNavWrapper}>
          <BottomNavBar
            items={VISIBLE_TABS.map((tab) => ({
              label: t(tab.labelKey),
              iconName: tab.iconName,
              iconNameInactive: tab.iconNameInactive,
              icon: tab.icon,
              flipX: tab.flipX,
              emphasized: tab.emphasized,
            }))}
            activeIndex={shellActiveTab}
            onTap={handleTabTap}
          />
        </div>
      )}

      {!isHostedByActivityHost && hasOverlayBackground && !isSheet && (
        <AnimatePresence>
          <motion.div
            key={overlayShellKey}
            variants={overlayVariants}
            initial="enter"
            animate={isOverlayClosing ? "exit" : "center"}
            exit="exit"
            transition={overlayTransition}
            className={styles.overlayShellMotion}
          >
            {appBar && (
              <AppBar
                variant={appBarVariant}
                title={appBarTitle}
                rightAction={appBarSlot.action}
                onBackPressed={closeOverlay}
                chromeAction={topStoreActivityAction === 'close' ? 'close' : 'back'}
              />
            )}
            <div className={styles.overlayMain}>
              <AnimatePresence initial={false} custom={isBack}>
                <motion.div
                  key={`${location.pathname}${location.search}`}
                  custom={isBack}
                  variants={overlayPageVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={overlayPageTransition}
                  className={styles.overlayRouteMotion}
                >
                  {outlet}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </AnimatePresence>
      )}

      {isSheet && (
        <Sheet
          open={!isOverlayClosing}
          onClose={closeOverlay}
          ariaLabel={appBarTitle}
          variant="tall"
          // 라우트 시트 — 시트 자체가 history entry 라 back = 라우트 pop 이 닫힘.
          // dialog 바인딩까지 걸면 sentinel 정리와 이중 pop 이 된다.
          bindBack={false}
        >
          {outlet}
        </Sheet>
      )}

      {exitingOverlay !== null && (
        <motion.div
          key={`exiting-${exitingOverlay.key}`}
          variants={overlayVariants}
          initial="center"
          animate="exit"
          transition={overlayTransition}
          className={styles.overlayShellMotion}
        >
          {exitingOverlay.appBar && (
            <AppBar
              variant={exitingOverlay.appBarVariant}
              title={exitingOverlay.appBarTitle}
              onBackPressed={() => undefined}
              chromeAction={topStoreActivityAction === 'close' ? 'close' : 'back'}
            />
          )}
          <div className={styles.overlayMain}>
            <div className={styles.overlayRouteMotion}>
              {exitingOverlay.outlet}
            </div>
          </div>
        </motion.div>
      )}

    </div>
  );

  // 데스크탑(≥1024)만 Astryx AppShell + SideNav/TopNav 로 감싼다.
  // CSS 로 숨기지 않고 렌더 분기하는 이유: Astryx 내부 DOM 구조에 의존하지 않기 위함.
  // shellInner 는 같은 엘리먼트 참조라 브레이크포인트를 넘나들어도 언마운트되지 않는다
  // (메인 탭 keep-alive 슬롯 상태 보존).
  if (!isDesktop) return shellInner;

  return (
    <AppShell
      height="fill"
      contentPadding={0}
      topNav={<DesktopTopNav />}
      sideNav={
        <DesktopSideNav
          // 아이콘은 TABS 의 iconName 에서 그대로 파생시킨다 — 데스크탑용 병렬 배열을
          // 따로 두면 모바일과 어긋난다 (실제로 어긋나 있었다: heroicons vs 자체 세트).
          tabs={VISIBLE_TABS.map((tab) => ({
            path: tab.path,
            label: t(tab.labelKey),
            icon: tab.iconName ? <Icon name={tab.iconName} size="lg" /> : undefined,
          }))}
          activeIndex={shellActiveTab}
          onTabTap={handleTabTap}
        />
      }
    >
      {shellInner}
    </AppShell>
  );
}

const pageVariants = {
  enter: (isBack: boolean) => ({
    x: isBack ? "-25%" : "100%",
  }),
  center: {
    x: 0,
  },
  exit: (isBack: boolean) => ({
    x: isBack ? "100%" : "-25%",
  }),
};

const noMotionVariants = {
  enter: {
    x: 0,
  },
  center: {
    x: 0,
  },
  exit: {
    x: 0,
  },
};

const pageTransition = {
  duration: 0.3,
  ease: [0.32, 0.72, 0, 1] as const,
};

const noMotionTransition = {
  duration: 0,
};

function isMainTabPath(pathname: string) {
  return VISIBLE_TABS.some((tab) => tab.path === pathname);
}

function getFallbackChrome(
  routeState: OverlayNavigationState | null,
  pathname: string,
): ChromeState {
  const rootPath =
    routeState?.overlayRootPath ??
    routeState?.overlayFallbackPath ??
    getFallbackPathname(pathname);

  // 탭 인덱스는 VISIBLE_TABS 에서 파생 (웹은 스캔 탭이 빠져 인덱스가 밀리므로 리터럴 금지)
  if (rootPath === "/search" || rootPath.startsWith("/search")) {
    return { appBar: true, appBarVariant: "home", appBarTitle: "nav.route.search", bottomNav: true, activeTab: tabIndexOf("/search") };
  }
  // 스캔 — 탭에서 들어오지만 화면은 상세처럼 다룬다 (2026-08-14).
  // 카메라가 화면을 가득 채우는 자리라 하단 탭이 뷰파인더 아래를 잘라먹고,
  // "지금 스캔 중"이라는 상태에서 다른 탭으로 새는 길을 열어 둘 이유도 없다.
  // 나가는 길은 화면 좌상단 뒤로가기 하나로 모은다 (ScanPage 의 .topBar).
  // 알림 화면이 먼저 쓴 것과 같은 패턴이다 (아래 참조).
  if (rootPath === "/scan" || rootPath.startsWith("/scan")) {
    return { appBar: false, appBarVariant: "home", bottomNav: true, activeTab: tabIndexOf("/scan") };
  }
  if (rootPath === "/collections" || rootPath.startsWith("/collections")) {
    return { appBar: true, appBarVariant: "home", appBarTitle: "nav.route.collection", bottomNav: true, activeTab: tabIndexOf("/collections") };
  }
  if (rootPath === "/me" || rootPath.startsWith("/me")) {
    return { appBar: true, appBarVariant: "myPage", appBarTitle: "nav.route.me", bottomNav: true, activeTab: tabIndexOf("/me") };
  }
  // 알림 — 탭이 아님 (상세 스타일)
  if (rootPath === "/notifications" || rootPath.startsWith("/notifications")) {
    return { appBar: true, appBarVariant: "home", appBarTitle: "nav.route.notifications", bottomNav: false, activeTab: 0 };
  }
  // 홈
  return { appBar: true, appBarVariant: "home", bottomNav: true, activeTab: 0 };
}

function getFallbackPathname(pathname: string) {
  if (pathname === "/me" || pathname.startsWith("/me/")) return "/me";
  if (pathname === "/notifications" || pathname.startsWith("/notifications/")) {
    return "/notifications";
  }
  if (pathname === "/search" || pathname.startsWith("/search/")) return "/search";
  if (pathname === "/scan" || pathname.startsWith("/scan/")) return "/scan";
  if (pathname === "/collections" || pathname.startsWith("/collections/")) return "/collections";
  return "/";
}

function getOverlayShellKey(
  pathname: string,
  routeState: OverlayNavigationState | null,
  currentKey: string | null,
) {
  if (routeState?.overlayStackId) return routeState.overlayStackId;

  if (pathname === "/support" || pathname.startsWith("/support/inquiry")) {
    return "/support-flow";
  }

  // `/me/settings` 는 2026-08-24 에 없앴다 (design) — 내 정보로 리다이렉트된다.
  if (pathname === "/me/profile") {
    return pathname;
  }

  return currentKey ?? pathname;
}

const overlayVariants = {
  enter: {
    x: "20%",
    opacity: 0,
  },
  center: {
    x: "0%",
    opacity: 1,
  },
  exit: {
    x: "20%",
    opacity: 0,
  },
};

const backgroundFadeVariants = {
  enter: { opacity: 1 },
  center: { opacity: 0 },
};

const overlayTransition = {
  duration: OVERLAY_EXIT_MS / 1000,
  ease: [0.22, 1, 0.36, 1] as const,
};

const overlayPageVariants = {
  enter: (isBack: boolean) => ({
    x: isBack ? "0%" : "100%",
    zIndex: isBack ? 0 : 2,
  }),
  center: {
    x: 0,
    zIndex: 1,
  },
  exit: (isBack: boolean) => ({
    x: isBack ? "100%" : "0%",
    zIndex: isBack ? 2 : 0,
  }),
};

const overlayPageTransition = {
  duration: 0.28,
  ease: [0.32, 0.72, 0, 1] as const,
};
